import { useEffect, useState } from "react";
import { useBrandContext } from "@/contexts/brand-context";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@/hooks/use-permissions";
import {
  useAiConfig,
  useAiModels,
  useUpdateAiConfig,
  type BrandAiConfig,
  type AiDefaultMode,
} from "@/hooks/use-ai-config";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export function BrandSettingsAiPage() {
  const { t } = useTranslation("settings");
  const { brand } = useBrandContext();
  const { canManageBrand } = usePermissions();
  const brandId = brand?.id ?? "";
  const { data: config, isLoading } = useAiConfig(brandId);
  const { data: models } = useAiModels();
  const updateConfig = useUpdateAiConfig(brandId);

  const MODE_OPTIONS: { value: AiDefaultMode; label: string; desc: string }[] = [
    { value: "fast", label: t("ai.modes.fast"), desc: t("ai.modeFast") },
    { value: "economy", label: t("ai.modes.economy"), desc: t("ai.modeEconomy") },
  ];

  const [draft, setDraft] = useState<BrandAiConfig | null>(null);
  useEffect(() => { if (config) setDraft(config); }, [config]);

  if (isLoading || !draft) {
    return (
      <div className="px-6 py-8 w-full space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(config);
  const llms = (models ?? []).filter((m) => m.kind === "llm");
  const imgs = (models ?? []).filter((m) => m.kind === "image");
  const vids = (models ?? []).filter((m) => m.kind === "video");

  async function save() {
    if (!draft) return;
    await updateConfig.mutateAsync(draft);
    toast.success(t("ai.saved"));
  }

  return (
    <div className="px-6 py-8 w-full space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">{t("ai.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("ai.subtitle")}</p>
      </header>

      <section className="rounded-lg border p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-1">
          <label className="block text-sm font-medium">{t("ai.llmModel")}</label>
          <select
            value={draft.llmModel ?? ""}
            disabled={!canManageBrand}
            onChange={(e) => setDraft({ ...draft, llmModel: e.target.value || null })}
            className="block w-full rounded border px-3 py-2 text-sm"
          >
            <option value="">{t("ai.selectPlaceholder")}</option>
            {llms.map((m) => <option key={m.id} value={m.id}>{m.name}{m.costLabel ? ` (${m.costLabel})` : ""}</option>)}
          </select>
          <p className="text-xs text-muted-foreground">{t("ai.llmModelHint")}</p>
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium">{t("ai.imageModel")}</label>
          <select
            value={draft.imageModel ?? ""}
            disabled={!canManageBrand}
            onChange={(e) => setDraft({ ...draft, imageModel: e.target.value || null })}
            className="block w-full rounded border px-3 py-2 text-sm"
          >
            <option value="">{t("ai.selectPlaceholder")}</option>
            {imgs.map((m) => <option key={m.id} value={m.id}>{m.name}{m.costLabel ? ` (${m.costLabel})` : ""}</option>)}
          </select>
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium">{t("ai.videoModel")}</label>
          <select
            value={draft.videoModel ?? ""}
            disabled={!canManageBrand}
            onChange={(e) => setDraft({ ...draft, videoModel: e.target.value || null })}
            className="block w-full rounded border px-3 py-2 text-sm"
          >
            <option value="">{t("ai.selectPlaceholder")}</option>
            {vids.map((m) => <option key={m.id} value={m.id}>{m.name}{m.costLabel ? ` (${m.costLabel})` : ""}</option>)}
          </select>
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium">{t("ai.defaultMode")}</label>
          <select
            value={draft.defaultMode ?? ""}
            disabled={!canManageBrand}
            onChange={(e) => setDraft({ ...draft, defaultMode: (e.target.value || null) as AiDefaultMode | null })}
            className="block w-full rounded border px-3 py-2 text-sm"
          >
            <option value="">{t("ai.selectPlaceholder")}</option>
            {MODE_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <p className="text-xs text-muted-foreground">
            {MODE_OPTIONS.find((m) => m.value === draft.defaultMode)?.desc ?? t("ai.defaultModeHint")}
          </p>
        </div>
      </section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={!dirty || updateConfig.isPending || !canManageBrand}>
          {updateConfig.isPending ? t("actions.saving", { ns: "common" }) : t("actions.save", { ns: "common" })}
        </Button>
      </div>
    </div>
  );
}
